/**
 * HOV CUSTOM: menubar-style click dropdowns for the header menu.
 *
 * Behavior model (per the shadcn/Radix Menubar reference, with one deliberate
 * deviation confirmed by the merchant): dropdowns are strictly click-driven.
 * Hover never opens, switches, or closes a panel. A panel closes only when:
 *   - a different parent trigger is clicked (its panel replaces the open one)
 *   - the open parent trigger is clicked again
 *   - Escape is pressed (focus returns to the trigger)
 *   - the user clicks outside the menubar
 *   - a link inside the panel is followed
 *   - keyboard focus leaves the menubar
 *
 * Markup contract (rendered by blocks/_header-menu.liquid, click mode):
 *   <header-menu class="header-menu hov-menubar">
 *     ... <div class="hov-menubar__anchor">
 *           <button class="hov-menubar__trigger" aria-expanded="false">…</button>
 *           <div class="hov-menubar__panel">…</div>
 *         </div> ...
 *   </header-menu>
 *
 * The panel is shown purely via CSS on [aria-expanded='true']; this script
 * only toggles that attribute and nudges panels back into the viewport
 * (--hov-shift-x), mirroring Radix's collision shifting.
 *
 * This file intentionally defines the `header-menu` element (normally defined
 * by header-menu.js, which is not loaded in click mode) so the header
 * section's element-selector CSS keeps matching.
 */

/** Viewport edge padding when shifting panels back into view, in px. */
const COLLISION_MARGIN = 16;

class HovMenubar extends HTMLElement {
  /** @type {HTMLButtonElement | null} The trigger of the open panel. */
  #openTrigger = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
    document.addEventListener('keydown', this.#onDocumentKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    this.#openTrigger = null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('button.hov-menubar__trigger');
    if (trigger instanceof HTMLButtonElement) {
      if (this.#openTrigger === trigger) {
        this.#close();
      } else {
        this.#open(trigger);
      }
      return;
    }

    // A link inside an open panel: let it navigate, but close the panel so
    // same-page (anchor) links don't leave it hanging open.
    if (this.#openTrigger && event.target.closest('a[href]')) {
      this.#close();
    }
  };

  /** @param {HTMLButtonElement} trigger */
  #open(trigger) {
    this.#close();

    trigger.setAttribute('aria-expanded', 'true');
    this.#openTrigger = trigger;

    const panel = trigger.nextElementSibling;
    if (panel instanceof HTMLElement) {
      this.#keepInViewport(panel);
    }

    // Parity with the stock menu so listeners (e.g. chat drawer) still react.
    this.dispatchEvent(new Event('megaMenu:hover', { bubbles: true }));
  }

  #close({ restoreFocus = false } = {}) {
    const trigger = this.#openTrigger;
    if (!trigger) return;

    trigger.setAttribute('aria-expanded', 'false');
    this.#openTrigger = null;

    const panel = trigger.nextElementSibling;
    if (panel instanceof HTMLElement) {
      panel.style.removeProperty('--hov-shift-x');
    }

    if (restoreFocus) trigger.focus();
  }

  /**
   * Shift a panel horizontally so it stays inside the viewport (Radix-style
   * collision handling). No shift is applied when it already fits, keeping
   * the panel exactly left-aligned with its trigger.
   * @param {HTMLElement} panel
   */
  #keepInViewport(panel) {
    panel.style.setProperty('--hov-shift-x', '0px');

    requestAnimationFrame(() => {
      if (!panel.isConnected) return;

      const rect = panel.getBoundingClientRect();
      let shift = Math.min(0, window.innerWidth - COLLISION_MARGIN - rect.right);
      if (rect.left + shift < COLLISION_MARGIN) {
        shift = COLLISION_MARGIN - rect.left;
      }

      if (shift !== 0) {
        panel.style.setProperty('--hov-shift-x', `${Math.round(shift)}px`);
      }
    });
  }

  /** Close when clicking anywhere outside the menubar. @param {Event} event */
  #onDocumentPointerDown = (event) => {
    if (!this.#openTrigger) return;
    if (event.composedPath().includes(this)) return;
    this.#close();
  };

  /** Close on Escape and hand focus back to the trigger. @param {KeyboardEvent} event */
  #onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#openTrigger) return;
    this.#close({ restoreFocus: true });
  };

  /** Close when keyboard focus leaves the menubar entirely. @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#openTrigger) return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (event.relatedTarget === null) return; // focus moved to the page/devtools, keep state
    this.#close();
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HovMenubar);
}
